import {
  AI_RESEARCH_ALLOCATION,
  MINING_ALLOCATION,
  STREAK_TIERS,
  TOTAL_SUPPLY,
} from "@gloxar/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError } from "../api/client";
import { keys, useClaim, useEpoch, useMe, useMyClaims, useSetReminders } from "../api/hooks";
import type { Bonuses, ClaimRecord } from "../api/types";
import { Countdown } from "../components/Countdown";
import {
  Button,
  Card,
  Empty,
  ErrorNote,
  Pill,
  Row,
  Skeleton,
  Stat,
  Switch,
} from "../components/ui";
import { gxr, multiplier, pct, shortDate } from "../lib/format";
import { tg } from "../telegram";

export function MineScreen() {
  const me = useMe();
  const claim = useClaim();
  const qc = useQueryClient();
  const [justClaimed, setJustClaimed] = useState<ClaimRecord | null>(null);

  if (me.isPending) return <MineSkeleton />;
  if (me.isError)
    return <ErrorNote>Could not load your account. {String(me.error.message)}</ErrorNote>;
  const d = me.data;

  const onClaim = async () => {
    tg.haptic.medium();
    try {
      const res = await claim.mutateAsync();
      setJustClaimed(res.claim);
      tg.haptic.success();
    } catch (err) {
      tg.haptic.error();
      if (err instanceof ApiError && err.code === "ALREADY_CLAIMED") return; // state refreshes
    }
  };

  const seasonState = d.claim.seasonState;

  return (
    <div className="space-y-3">
      <Card>
        <div className="flex items-start justify-between">
          <Stat
            label="Your balance"
            value={<span className="text-3xl">{gxr(d.balance)} GXR</span>}
          />
          {d.rank && <Pill tone="brand">#{d.rank}</Pill>}
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          <Stat
            label="Streak"
            value={
              <span className={d.streak.alive ? "text-brand" : ""}>
                {d.streak.current}
                <span className="text-sm font-normal text-hint"> d</span>
              </span>
            }
            sub={
              d.streak.nextTier
                ? `${pct(d.streak.nextTier.bonusBps, true)} at day ${d.streak.nextTier.day}`
                : "max tier"
            }
          />
          <Stat
            label="Epoch"
            value={d.epoch ? `${d.epoch.id} / 6` : "—"}
            sub={d.epoch ? `base ${gxr(d.epoch.baseReward)}` : ""}
          />
          <Stat
            label="Referrals"
            value={d.referrals.activeDirect}
            sub={`${d.referrals.activeSecondary} secondary`}
          />
        </div>
      </Card>

      {justClaimed && (
        <Card className="border border-brand/40">
          <p className="text-center text-sm text-hint">Claimed</p>
          <p className="text-center text-3xl font-bold text-brand">
            +{gxr(justClaimed.amount)} GXR
          </p>
          <Breakdown
            base={justClaimed.baseReward}
            bonuses={justClaimed.bonuses}
            total={justClaimed.totalMultiplierBps}
            streakDay={justClaimed.streakDay}
          />
        </Card>
      )}

      <Card>
        {seasonState === "active" && d.claim.available && d.projection && (
          <>
            <p className="mb-1 text-center text-sm text-hint">Today's reward</p>
            <p className="mb-3 text-center text-4xl font-bold">{gxr(d.projection.amount)} GXR</p>
            <Button onClick={onClaim} disabled={claim.isPending}>
              {claim.isPending ? "Claiming…" : "Claim"}
            </Button>
          </>
        )}
        {seasonState === "active" && !d.claim.available && (
          <>
            <p className="mb-1 text-center text-sm text-hint">Next claim in</p>
            <p className="mb-3 text-center text-4xl font-bold">
              <Countdown
                to={d.claim.nextClaimAt}
                onDone={() => void qc.invalidateQueries({ queryKey: keys.me })}
              />
            </p>
            <Button disabled variant="secondary">
              Claimed today
            </Button>
          </>
        )}
        {seasonState === "not_started" && <StartCountdown />}
        {seasonState === "ended" && (
          <Empty>The mining season has ended. Your balance is final pending the snapshot.</Empty>
        )}
        {seasonState === "not_configured" && <Empty>Mining is not configured.</Empty>}
        {claim.isError &&
          !(claim.error instanceof ApiError && claim.error.code === "ALREADY_CLAIMED") && (
            <div className="mt-3">
              <ErrorNote>
                {claim.error instanceof Error ? claim.error.message : "Claim failed"}
              </ErrorNote>
            </div>
          )}
      </Card>

      <AboutCard />

      {d.projection && (
        <Card title={d.claim.claimedToday ? "Tomorrow's breakdown" : "Today's breakdown"}>
          <Breakdown
            base={d.epoch?.baseReward ?? "0"}
            bonuses={d.projection.bonuses}
            total={d.projection.totalMultiplierBps}
            streakDay={d.projection.streakDay}
          />
        </Card>
      )}

      <Card title="Streak tiers">
        <div className="grid grid-cols-4 gap-2 text-center text-xs">
          {STREAK_TIERS.map((t) => {
            const reached = d.streak.alive && d.streak.current >= t.minDay;
            return (
              <div
                key={t.minDay}
                className={`rounded-lg p-2 ${reached ? "bg-brand/15 text-brand" : "bg-bg-2 text-hint"}`}
              >
                <div className="font-semibold">day {t.minDay}+</div>
                <div>{pct(t.bonusBps, true)}</div>
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-hint">
          Miss a day and the streak resets to day 1. Claims reset at 00:00 UTC.
        </p>
      </Card>

      <ReminderCard enabled={d.settings.remindersEnabled} />

      <History />
    </div>
  );
}

/** What GLOXAR is for: the AI research allocation and the staking benefit. */
function AboutCard() {
  const aiPct = Number((AI_RESEARCH_ALLOCATION * 100n) / TOTAL_SUPPLY);
  const miningPct = Number((MINING_ALLOCATION * 100n) / TOTAL_SUPPLY);
  return (
    <Card title="About GLOXAR">
      <p className="text-sm">
        GLOXAR is an AI project. <span className="font-semibold text-brand">{aiPct}%</span> of all
        GXR funds AI and AGI research to build GLOXAR AI; {miningPct}% is mined here by the
        community.
      </p>
      <div className="mt-3 rounded-lg bg-brand/10 px-3 py-2 text-sm">
        <span className="font-semibold text-brand">Stake GXR, use GLOXAR AI for free.</span>{" "}
        <span className="text-hint">
          After TGE, stakers get free access to GLOXAR AI when it launches.
        </span>
      </div>
    </Card>
  );
}

function Breakdown({
  base,
  bonuses,
  total,
  streakDay,
}: {
  base: string;
  bonuses: Bonuses;
  total: number;
  streakDay: number;
}) {
  return (
    <div className="mt-2">
      <Row label="Base reward" value={`${gxr(base)} GXR`} />
      <Row
        label={`Streak (day ${streakDay})`}
        value={pct(bonuses.streakBps, true)}
        muted={bonuses.streakBps === 0}
      />
      <Row
        label="Referrals"
        value={pct(bonuses.referralBps, true)}
        muted={bonuses.referralBps === 0}
      />
      <Row
        label="Verified wallet"
        value={pct(bonuses.verifiedBps, true)}
        muted={bonuses.verifiedBps === 0}
      />
      <div className="my-1 border-t border-white/5" />
      <Row label="Multiplier" value={multiplier(total)} />
    </div>
  );
}

/** Live countdown to the season start; refetches `me` when it passes so the Claim button appears. */
function StartCountdown() {
  const epoch = useEpoch();
  const qc = useQueryClient();
  const startsAt = epoch.data?.startsAt ?? epoch.data?.season?.startsAt;
  if (!startsAt)
    return <Empty>Mining has not started yet. Check the Epochs tab for the start date.</Empty>;
  return (
    <>
      <p className="mb-1 text-center text-sm text-hint">Mining starts in</p>
      <p className="mb-1 text-center text-4xl font-bold">
        <Countdown
          to={startsAt}
          onDone={() => {
            void qc.invalidateQueries({ queryKey: keys.me });
            void qc.invalidateQueries({ queryKey: keys.epoch });
          }}
        />
      </p>
      <p className="text-center text-xs text-hint">{shortDate(startsAt)} · 00:00 UTC</p>
    </>
  );
}

function ReminderCard({ enabled }: { enabled: boolean }) {
  const set = useSetReminders();
  return (
    <Card>
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-semibold">Claim reminders</p>
          <p className="mt-0.5 text-xs text-hint">
            A message from @gloxar_bot a few hours before the reset if you have not claimed yet.
          </p>
        </div>
        <Switch
          checked={enabled}
          disabled={set.isPending}
          label="Claim reminders"
          onChange={(next) => {
            tg.haptic.select();
            set.mutate(next);
          }}
        />
      </div>
    </Card>
  );
}

function History() {
  const claims = useMyClaims();
  if (claims.isPending) return <Skeleton className="h-24" />;
  if (claims.isError || !claims.data.claims.length) return null;
  return (
    <Card title="Recent claims">
      <ul className="divide-y divide-white/5">
        {claims.data.claims.slice(0, 10).map((c) => (
          <li key={c.id} className="flex items-center justify-between py-2 text-sm">
            <span className="text-hint">
              {shortDate(c.claimDate)} · day {c.streakDay} · {multiplier(c.totalMultiplierBps)}
            </span>
            <span className="font-medium tabular-nums">+{gxr(c.amount)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function MineSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-36" />
      <Skeleton className="h-40" />
      <Skeleton className="h-44" />
    </div>
  );
}
